import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  Observable,
  catchError,
  defer,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import { API_V1_URL } from '../../../core/config/api.config';
import { AuthService } from '../../../core/services/auth.service';
import {
  EntlastungDetail,
  EntlastungSaveRequest,
  EntlastungUebersicht,
  EntlastungsArtikel,
  Entlastungsgrund,
} from '../models/entlastung.model';
import {
  EntlastungDetailDto,
  EntlastungOverviewDto,
  EntlastungsArtikelDto,
  EntlastungsgrundDto,
  mapEntlastungArtikel,
  mapEntlastungDetail,
  mapEntlastungGruende,
  mapEntlastungOverview,
  mapEntlastungSave,
} from './entlastungen.mapper';

@Injectable({ providedIn: 'root' })
export class EntlastungenService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiBaseUrl = API_V1_URL;
  private readonly anzahlOffenState = signal<number | undefined>(undefined);

  private readonly articleRequests = new Map<number, Observable<EntlastungsArtikel[]>>();
  private readonly pendingDetails = new Map<number, number>();
  private readonly overviewNames = new Map<number, string>();
  private overviewToken: string | null = null;

  readonly anzahlOffen = this.anzahlOffenState.asReadonly();

  getOverview(): Observable<EntlastungUebersicht[]> {
    return this.http.get<EntlastungOverviewDto[]>(`${this.apiBaseUrl}/entlastungen`).pipe(
      map(mapEntlastungOverview),
      tap((items) => {
        this.overviewToken = this.authService.getToken();
        this.overviewNames.clear();
        for (const item of items) {
          const name = item.filialName.trim();
          if (name) this.overviewNames.set(item.filialNummer, name);
        }
      })
    );
  }

  getAnzahlOffen(): Observable<number> {
    return this.http
      .get<number>(`${this.apiBaseUrl}/entlastungen/anzahl-offen`)
      .pipe(tap((anzahl) => this.anzahlOffenState.set(anzahl)));
  }

  getDetail(filialNummer: number): Observable<EntlastungDetail> {
    return defer(() => {
      this.pendingDetails.set(filialNummer, (this.pendingDetails.get(filialNummer) ?? 0) + 1);
      return this.http.get<EntlastungDetailDto>(
        `${this.apiBaseUrl}/entlastungen/${filialNummer}`
      ).pipe(
        switchMap((item) => {
          const token = this.authService.getToken();
          const cachedName = token && token === this.overviewToken
            ? this.overviewNames.get(filialNummer)
            : undefined;
          const authorizedName = this.authService.getAllowedFilialenDetails().find(
            (filiale) => filiale.filialNummer === filialNummer && filiale.aktiv !== false
          )?.name?.trim();
          const filialName = cachedName || authorizedName || `Filiale ${filialNummer}`;

          return item.positionen.length === 0
            ? of(mapEntlastungDetail(item, filialName, []))
            : this.getVerfuegbareArtikel(filialNummer).pipe(
                map((artikel) => mapEntlastungDetail(item, filialName, artikel))
              );
        }),
        finalize(() => {
          const pending = this.pendingDetails.get(filialNummer) ?? 1;
          if (pending === 1) {
            this.pendingDetails.delete(filialNummer);
            this.articleRequests.delete(filialNummer);
          } else {
            this.pendingDetails.set(filialNummer, pending - 1);
          }
        })
      );
    });
  }

  getGruende(filialNummer: number): Observable<Entlastungsgrund[]> {
    return this.http.get<EntlastungsgrundDto[]>(
      `${this.apiBaseUrl}/entlastungen/${filialNummer}/gruende`
    ).pipe(map(mapEntlastungGruende));
  }

  getVerfuegbareArtikel(filialNummer: number): Observable<EntlastungsArtikel[]> {
    const cached = this.articleRequests.get(filialNummer);
    if (cached) {
      return cached;
    }

    const request = this.http.get<EntlastungsArtikelDto[]>(
      `${this.apiBaseUrl}/entlastungen/${filialNummer}/verfuegbare-artikel`
    ).pipe(
      map(mapEntlastungArtikel),
      finalize(() => {
        if (!this.pendingDetails.has(filialNummer)) {
          this.articleRequests.delete(filialNummer);
        }
      }),
      shareReplay(1)
    );
    this.articleRequests.set(filialNummer, request);
    return request;
  }

  saveEntlastung(
    filialNummer: number,
    request: EntlastungSaveRequest
  ): Observable<void> {
    return defer(() => this.http.put<void>(
      `${this.apiBaseUrl}/entlastungen/${filialNummer}`,
      mapEntlastungSave(request)
    )).pipe(
      switchMap(() => this.getAnzahlOffen().pipe(
        catchError(() => of(undefined)),
        map(() => void 0)
      ))
    );
  }
}
